import { logger, LOGS } from "../../utils";
import { ApiManager } from "../api";
import { toDomainAcceptedTodo } from "../api/mappers/todo.mapper";
import type { TodoItem } from "../mail/types/mail";

export class UserTodos {
    private apiManager: ApiManager;

    private fetchingUserTodos: Promise<any> | null = null;

    private fetchingDashboardUserTodos: Promise<any> | null = null;

    private usertodosRecordById: Record<string, TodoItem> | null = null;

    constructor(apiManager: ApiManager) {
        this.apiManager = apiManager;
    }

    public replaceAll(userTodos: TodoItem[]): void {
        this.usertodosRecordById = {};
        userTodos.forEach((todo) => {
            this.usertodosRecordById![todo.id] = todo;
        });
    }

    public _cleanup(): void {
        if (this.fetchingUserTodos) {
            logger.info(
                LOGS.UserManager,
                "    Cancelling pending user todos fetch",
            );
            this.fetchingUserTodos = null;
        }

        if (this.fetchingDashboardUserTodos) {
            logger.info(
                LOGS.UserManager,
                "    Cancelling pending dashboard user todos fetch",
            );
            this.fetchingDashboardUserTodos = null;
        }

        this.usertodosRecordById = null;
    }

    /**
     * Get all todos from DB
     * @returns Promise with array of todo items
     */
    public async getUserTodos(
        forceFetchTodosFromDb: boolean = false,
    ): Promise<TodoItem[]> {
        logger.info(LOGS.UserManager, "getUserTodos()");

        // Si une requête est déjà en cours, attendez son résultat
        if (this.fetchingUserTodos) {
            logger.info(
                LOGS.UserManager,
                `   => Waiting for ongoing userTodos fetch ...`,
            );
            return this.fetchingUserTodos;
        }

        logger.info(LOGS.UserManager, `   => Fetching userTodos`);
        this.fetchingUserTodos = this.fetchUserTodos(forceFetchTodosFromDb);

        try {
            const userTodos = await this.fetchingUserTodos;
            logger.info(LOGS.UserManager, `userTodos`, userTodos);

            logger.info(LOGS.UserManager, `   => Returning userTodos`);
            return userTodos;
        } catch (error) {
            logger.error(
                LOGS.UserManager,
                `   => Error fetching userTodos ! ${error}`,
            );
            throw new Error(`   [Error fetching userTodos : ` + error);
        } finally {
            logger.info(
                LOGS.UserManager,
                `   => Reseting this.fetchingUserTodos`,
            );
            this.fetchingUserTodos = null;
        }
    }

    /**
     * Get user todos to be displayed on dashboard
     * @returns Promise with array of todo items
     */
    public async getDashboardUserTodos(): Promise<TodoItem[]> {
        logger.info(LOGS.UserManager, "getDashboardUserTodos()");

        // Si une requête est déjà en cours, attendez son résultat
        if (this.fetchingDashboardUserTodos) {
            logger.info(
                LOGS.UserManager,
                `   => Waiting for ongoing userTodos fetch ...`,
            );
            return this.fetchingDashboardUserTodos;
        }

        logger.info(LOGS.UserManager, `   => Fetching userTodos for Dashboard`);
        this.fetchingDashboardUserTodos = this.fetchUserTodos(false);

        try {
            const userTodos = await this.fetchingDashboardUserTodos;
            logger.info(LOGS.UserManager, `dashboard userTodos`, userTodos);

            logger.info(
                LOGS.UserManager,
                `   => Returning dashboard userTodos`,
            );
            const userDashboardTodos = userTodos.filter(
                (todo: TodoItem) => !todo.done,
            );

            return userDashboardTodos;
        } catch (error) {
            logger.error(
                LOGS.UserManager,
                `   => Error fetching dashboard userTodos ! ${error}`,
            );
            throw new Error(
                `   [Error fetching dashboard userTodos : ` + error,
            );
        } finally {
            logger.info(
                LOGS.UserManager,
                `   => Reseting this.fetchingDashboardUserTodos`,
            );
            this.fetchingDashboardUserTodos = null;
        }
    }

    private async fetchUserTodos(
        forceFetchNewProfile: boolean = false,
    ): Promise<TodoItem[]> {
        logger.info(LOGS.MailManager, "fetchUserTodos()");

        let todos: TodoItem[] = [];

        try {
            // If we have cached todos, return them
            if (this.usertodosRecordById && !forceFetchNewProfile) {
                logger.info(LOGS.MailManager, "   Using cached todos.");
                todos = Object.values(this.usertodosRecordById);
            }
            // Else, fetch from DB
            else {
                this.usertodosRecordById = {};

                logger.info(LOGS.MailManager, "  Fetching todos from DB ...");
                todos = (await this.apiManager.getUserTodoItems()).map(
                    toDomainAcceptedTodo,
                );
                logger.info(
                    LOGS.MailManager,
                    `  Todos fetched from Db.`,
                    todos,
                );

                todos.forEach((todo) => {
                    this.usertodosRecordById![todo.id] = todo;
                });
            }

            return todos;
        } catch (error) {
            const errorMessage =
                error instanceof Error ? error.message : String(error);
            const errorText = `Error retrieving user todos: ${errorMessage}`;
            logger.error(LOGS.MailManager, errorText);
            throw new Error(errorText);
        }
    }
}
